import { FC, ReactElement } from 'react';

import { AccountDashboardConfig } from '../../containers';
import { Customer } from '../../entities/@account/Customer/Customer';
import { Address } from '../../entities/Address/Address';
import { areObjectsEqual } from '../../helpers/object';
import { authenticateUser } from '../../redux/@account/authentication/authenticationActions';
import {
    addAddress,
    updateCustomer,
    updateCustomerAddress,
    updateDefaultAddress,
} from '../../redux/@account/customer/customerActions';
import { useTypedDispatch, useTypedSelector } from '../../redux/store';

export const ConnectedAccountDashboardConfig: FC = (): ReactElement | null => {
    const dispatch = useTypedDispatch();

    const isSuccessful = useTypedSelector(state => state.customerSlice.isSuccessful);
    const customer = useTypedSelector(state => state.customerSlice.customer);

    if (!customer) {
        return null;
    }

    const handleUpdateCustomer = async (newCustomerData: Customer): Promise<void> => {
        const { password, ...body } = newCustomerData;
        const updatedCustomer = { ...customer, ...newCustomerData };

        const hasChanges = !areObjectsEqual(customer, body);

        if (!hasChanges) {
            return;
        }

        await dispatch(updateCustomer(body));

        const emailChanged = updatedCustomer.email !== customer?.email;

        if (emailChanged && password) {
            await dispatch(authenticateUser({
                email: updatedCustomer.email,
                password,
                rememberMe: true,
            }));
        }
    };

    const handleUpdateAddresses = (newAddressesData: Address): void => {
        const matchingAddress = customer.addresses.find(address => address.id === newAddressesData.id);

        if (matchingAddress) {
            const addressHasActualChanges = !areObjectsEqual(matchingAddress, newAddressesData);

            if (addressHasActualChanges) {
                dispatch(updateCustomerAddress(newAddressesData));
            }
        }

        dispatch(addAddress(newAddressesData));
    };

    const setDefaultAddress = (id: string): void => {
        dispatch(updateDefaultAddress(id));
    };

    return (
        <AccountDashboardConfig
            successfullyAddedAddress={isSuccessful}
            customer={customer}
            onUpdateCustomer={handleUpdateCustomer}
            onUpdateAddresses={handleUpdateAddresses}
            onChangeShippingAddress={setDefaultAddress}
        />
    );
};
